import { useCallback, useEffect, useRef, useState, type ClipboardEvent } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';

import { fileProblem, named, type AttachmentRules, type UploadedFile } from './attachmentFiles';

export interface Draft<T extends UploadedFile> {
  key: string;
  name: string;
  size: number;
  /** A local preview while it uploads, for images. */
  preview: string | null;
  state: 'uploading' | 'done' | 'error';
  attachment?: T;
  /** Already the record's: removing it takes nothing back from the server. */
  kept?: boolean;
  error?: string;
}

function keptDraft<T extends UploadedFile>(attachment: T, key: string): Draft<T> {
  return {
    key,
    name: attachment.filename,
    size: attachment.size_bytes,
    preview: null,
    state: 'done',
    attachment,
    kept: true,
  };
}

function pickedDraft<T extends UploadedFile>(file: File, key: string, problem: string | null): Draft<T> {
  const isImage = file.type.startsWith('image/');
  return {
    key,
    name: file.name,
    size: file.size,
    preview: isImage && !problem ? URL.createObjectURL(file) : null,
    state: problem ? 'error' : 'uploading',
    error: problem ?? undefined,
  };
}

function patched<T extends UploadedFile>(key: string, patch: Partial<Draft<T>>) {
  return (list: Draft<T>[]) => list.map((d) => (d.key === key ? { ...d, ...patch } : d));
}

/** Lets go of a draft's preview and, unless the record already held it, takes its upload back. */
function release<T extends UploadedFile>(draft: Draft<T>, remove: UseMutationResult<void, Error, string>) {
  if (draft.preview) URL.revokeObjectURL(draft.preview);
  if (draft.attachment && !draft.kept) remove.mutate(draft.attachment.id);
}

export function useAttachmentDraft<T extends UploadedFile>({
  rules,
  upload,
  remove,
  initial = [],
}: {
  rules: AttachmentRules;
  upload: UseMutationResult<T, Error, File>;
  remove: UseMutationResult<void, Error, string>;
  initial?: T[];
}) {
  const counter = useRef(0);
  const [drafts, setDrafts] = useState<Draft<T>[]>(() =>
    initial.map((a) => keptDraft(a, `k${(counter.current += 1)}`)),
  );
  // The latest list and props, read from callbacks that keep one identity and from the unmount cleanup.
  const latest = useRef<Draft<T>[]>(drafts);
  latest.current = drafts;
  const props = useRef({ rules, upload, remove });
  props.current = { rules, upload, remove };

  const add = useCallback((files: File[]) => {
    const { rules: current, upload: uploader } = props.current;
    const room = current.maxFiles - latest.current.filter((d) => d.state !== 'error').length;
    files.slice(0, Math.max(0, room)).forEach((raw) => {
      const file = named(raw);
      const key = `f${(counter.current += 1)}`;
      const problem = fileProblem(file, current);
      setDrafts((list) => [...list, pickedDraft<T>(file, key, problem)]);
      if (problem) return;
      uploader
        .mutateAsync(file)
        .then((attachment) => setDrafts(patched<T>(key, { state: 'done', attachment, name: attachment.filename })))
        .catch((error: unknown) =>
          setDrafts(patched<T>(key, { state: 'error', error: error instanceof Error ? error.message : 'Upload failed' })),
        );
    });
  }, []);

  function discard(key: string) {
    const draft = latest.current.find((d) => d.key === key);
    if (draft) release(draft, remove);
    setDrafts((list) => list.filter((d) => d.key !== key));
  }

  /** After saving: the files now belong to the record; just let go of the previews. */
  function sent() {
    latest.current.forEach((d) => d.preview && URL.revokeObjectURL(d.preview));
    latest.current = [];
    setDrafts([]);
  }

  // Leaving without saving takes the uploads back (best effort; the server sweeps the rest).
  useEffect(() => () => latest.current.forEach((d) => release(d, props.current.remove)), []);

  /** Paste a screenshot or a copied file; plain text pastes as usual. */
  function onPaste(event: ClipboardEvent) {
    const files = Array.from(event.clipboardData.files);
    if (files.length === 0) return;
    event.preventDefault();
    add(files);
  }

  const ready = drafts.flatMap((d) => (d.state === 'done' && d.attachment ? [d.attachment] : []));
  return {
    rules,
    drafts,
    add,
    discard,
    sent,
    onPaste,
    /** The files that landed, kept ones included: what the form sends. */
    files: ready,
    ids: ready.map((a) => a.id),
    busy: drafts.some((d) => d.state === 'uploading'),
    full: drafts.filter((d) => d.state !== 'error').length >= rules.maxFiles,
  };
}

export type AttachmentDraft = ReturnType<typeof useAttachmentDraft<UploadedFile>>;
